import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Logo from '@/components/Logo';
import ScrollArea from '@/components/ScrollArea';
import { useUploadPdfButton } from '@/components/UploadPDFButton/hooks';
import { useIsCollaborator, useIsExtendedCollaborator } from '@/hooks/user';
import LogoutIcon from '@/icons/logout.svg';
import routes from '@/routes';
import { logout } from '@/store/profile/thunks';
import { setIsModalContactOpen } from '@/store/site';
import { cn, showError, showSuccess } from '@/utils';

import getSitemap from '../sitemap';

function MenuContent({ closeMenu }) {
	const [open, setOpen] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const user = useSelector(state => state.profile.user);
	const isCollaborator = useIsCollaborator();
	const isExtendedCollaborator = useIsExtendedCollaborator();
	const sitemap = getSitemap(isCollaborator, isExtendedCollaborator);

	const {
		inputProps,
		loading: uploadPdfLoading,
		onClick: onUploadPdfButtonClick
	} = useUploadPdfButton();

	const currentUrl = window.location.pathname;

	const handleClickContactButton = () => {
		closeMenu();
		dispatch(setIsModalContactOpen(true));
	};

	const handleLogout = async () => {
		try {
			setOpen(false);
			await dispatch(logout()).unwrap();
			showSuccess(t('common.logoutSuccess'));
			navigate(routes.home);
		} catch (error) {
			showError(error);
		}
	};

	const renderItemContent = item => (
		<>
			<item.icon
				aria-hidden
				className={cn(
					'text-grey-300 group-hover:text-grey-200',
					currentUrl.includes(item.to) && 'text-red'
				)}
			/>
			<span
				className={cn(
					'text-sm font-semibold leading-6 text-grey-300 group-hover:text-grey-200',
					currentUrl.includes(item.to) && 'text-white'
				)}
			>
				{t(item.text)}
			</span>
		</>
	);

	const renderLogo = () => (
		<div className="container flex items-center px-3">
			<Logo />
		</div>
	);

	const renderTopSection = () => (
		<div className="flex w-full grow flex-col justify-start gap-2 pt-3">
			{sitemap[0].links.map((item, i) =>
				item.to === routes.newDocument ? (
					<div className="w-full" key={item.to}>
						<Button
							className="group w-full justify-start gap-2 px-3 py-1 hover:bg-transparent"
							onClick={onUploadPdfButtonClick}
							loading={uploadPdfLoading}
						>
							{renderItemContent(item)}
						</Button>
						<input {...inputProps} />
					</div>
				) : (
					<Link
						className="justify-start px-3 py-2 text-left text-sm leading-7 text-white transition-colors hover:text-red"
						to={item.to}
						key={`sitemap-item-${i}`}
						onClick={closeMenu}
					>
						<div className="group flex gap-2">
							{renderItemContent(item)}
						</div>
					</Link>
				)
			)}
		</div>
	);

	const renderBottomSection = () => (
		<div className="flex w-full flex-col justify-start">
			{sitemap[1].links.map((item, i) =>
				item.to === routes.contact ? (
					<div key={item.to}>
						<Button
							className="group w-full justify-start gap-2 px-3 py-1 hover:bg-transparent"
							onClick={handleClickContactButton}
							type="button"
							variant="ghost"
						>
							{renderItemContent(item)}
						</Button>
						<div className="my-3 h-[1px] w-full bg-grey-500" />
					</div>
				) : (
					<Link
						className="justify-start px-3 py-1 text-left text-sm leading-7 text-white transition-colors hover:text-red"
						key={`sitemap-item-${i}`}
						onClick={closeMenu}
						to={item.to}
					>
						<div className="group flex gap-2">
							{renderItemContent(item)}
						</div>
					</Link>
				)
			)}
			<div className="my-3 h-[1px] w-full bg-grey-500" />

			<div className="grid w-full gap-2">
				<p className="truncate px-3 text-xs leading-5 text-grey-500">
					{user?.email || ''}
				</p>
				<Button
					type="button"
					className="flex w-fit items-center gap-2 rounded px-3 py-1 text-xs font-semibold text-grey-500 transition-colors hover:bg-white/10 hover:text-red"
					onClick={() => setOpen(true)}
					variant="ghost"
				>
					<LogoutIcon aria-hidden />
					<span>{t('common.logout')}</span>
				</Button>
			</div>
		</div>
	);

	return (
		<ScrollArea maxHeight="100%">
			<div className="flex h-dvh flex-col gap-5 bg-black px-4 py-5">
				{renderLogo()}
				{renderTopSection()}
				{renderBottomSection()}
			</div>

			<ConfirmationModal
				open={open}
				onClose={() => setOpen(false)}
				title={t('common.logout')}
				text={t('common.logoutConfirmation')}
				confirmText={t('common.yesLogout')}
				onConfirm={handleLogout}
			/>
		</ScrollArea>
	);
}

export default MenuContent;
